// @flow

import * as React from 'react';
import {useIntl} from 'react-intl';
import {useAnalytics} from 'docs/data';
import {DialogContent, DialogFooter, DialogHeader} from 'sail/Dialog';
import Button, {ButtonGroup, ButtonLink} from 'sail/Button';
import {TextArea} from 'sail/Input';
import Group from 'sail/Group';
import ContextualPopover from 'sail/Layer/ContextualPopover';
import {Text, Body} from 'sail/Text';
import CluiButtonLink from '../ButtonLink';
import FeedbackIcon from './FeedbackIcon';
import messages from './messages';

import './FeedbackAction.scss';

const initialState = {
  isPositive: null,
  response: '',
  isActive: false,
};

type State = {
  isPositive: ?boolean,
  response: string,
  isActive: boolean,
};

export const FeedbackAction = (): React.Node => {
  const {trackAction} = useAnalytics();
  const {formatMessage} = useIntl();

  const [feedback, setFeedback] = React.useState<State>(initialState);
  const feedbackActionRef = React.useRef(null);

  const handleThumbsDown = () => {
    setFeedback((prevState) => ({...prevState, isPositive: false}));
  };

  const handleThumbsUp = () => {
    setFeedback((prevState) => ({...prevState, isPositive: true}));
  };

  const reset = () => {
    setFeedback((prevState) => ({...prevState, isActive: false}));
  };

  const handleSubmit = () => {
    trackAction('stripe_clui_feedback', {
      isPositive: feedback.isPositive,
      response: feedback.response,
    });

    reset();
  };

  const title = (
    <Group spacing={12} alignItems="center" direction="horizontal">
      <span>{feedback.isPositive ? '👍' : '👎'}</span>
      <span>
        {formatMessage(
          feedback.isPositive
            ? messages.thumbsUpTitle
            : messages.thumbsDownTitle,
        )}
      </span>
    </Group>
  );

  const contents =
    typeof feedback.isPositive === 'boolean' ? (
      <form>
        <DialogHeader title={title} />
        <DialogContent padding={20}>
          <TextArea
            id="clui-feedback"
            autoFocus
            onChange={(response) =>
              setFeedback((prevState) => ({...prevState, response}))
            }
            onFocus={(e) => e.stopPropagation()}
            aria-label={formatMessage(messages.optionalResponsePlaceholder)}
            placeholder={formatMessage(messages.optionalResponsePlaceholder)}
            value={feedback.response}
            resizeable={false}
          />
        </DialogContent>
        <DialogFooter
          end={
            <ButtonGroup>
              <Button
                color="white"
                onClick={reset}
                label={formatMessage(messages.cancelLabel)}
              />
              <Button
                color="blue"
                onClick={handleSubmit}
                label={formatMessage(messages.submitLabel)}
              />
            </ButtonGroup>
          }
        />
      </form>
    ) : (
      <Group
        direction="horizontal"
        alignItems="center"
        spacing={16}
        style={{padding: 16}}
      >
        <Body>{formatMessage(messages.feedbackPromptLabel)}</Body>
        <Group direction="horizontal" alignItems="center" spacing={16}>
          <ButtonLink
            label={<Text size={20}>{'👍'}</Text>}
            onClick={handleThumbsUp}
            className="FeedbackActionThumb"
          />
          <ButtonLink
            label={<Text size={20}>{'👎'}</Text>}
            onClick={handleThumbsDown}
            className="FeedbackActionThumb"
          />
        </Group>
      </Group>
    );

  return (
    <div className="FeedbackAction">
      <div ref={feedbackActionRef}>
        <CluiButtonLink
          label={<FeedbackIcon />}
          tip="Feedback"
          onClick={() =>
            setFeedback((prevState) => ({...initialState, isActive: true}))
          }
        />
      </div>
      {feedbackActionRef.current && (
        <ContextualPopover
          arrowStyle={{display: 'none'}}
          autoflip
          context={{element: feedbackActionRef.current}}
          contextMeetingPoint="top"
          layerMeetingPoint="bottom"
          offsetY={-8}
          onClickOutside={() => {
            reset();
            return 'handled';
          }}
          shown={feedback.isActive}
          variableWidth
          trapFocus
        >
          {contents}
        </ContextualPopover>
      )}
    </div>
  );
};

export default FeedbackAction;
